import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import comprehensive5MinPrediction from '../data/comprehensivePrediction';

// Use comprehensive 5-minute prediction
const createStoryScript = (lang = 'hinglish') => {
  return {
    text: comprehensive5MinPrediction.text,
    icons: comprehensive5MinPrediction.planetaryKeywords,
    domainIcons: comprehensive5MinPrediction.domainIcons,
    timeline: comprehensive5MinPrediction.timeline
  };
};

const PastStoryUnifiedCinematic = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [storyWords, setStoryWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [visibleIcons, setVisibleIcons] = useState([]);
  const [visibleTimelineEvents, setVisibleTimelineEvents] = useState([]);
  
  const storyScript = createStoryScript(selectedLanguage);
  const wordIntervalRef = useRef(null);
  const predictionId = localStorage.getItem('prediction_id');

  // Initialize story words
  useEffect(() => {
    const words = storyScript.text.split(' ').map((word, idx) => ({
      text: word,
      index: idx,
      isHighlight: storyScript.icons.some(icon => 
        word.toUpperCase().includes(icon.keyword) || word.includes(icon.keyword)
      )
    }));
    setStoryWords(words);
  }, [selectedLanguage]);

  // Demo loading simulation
  useEffect(() => {
    if (!predictionId) {
      toast.error('No prediction ID found');
      navigate('/onboarding-new');
      return;
    }

    const isDemoId = predictionId.startsWith('demo_');
    
    if (isDemoId) {
      let currentProgress = 0;
      const demoInterval = setInterval(() => {
        currentProgress += 5;
        setProgress(currentProgress);

        if (currentProgress >= 100) {
          clearInterval(demoInterval);
          setTimeout(() => {
            toast.success('Your story is ready!');
            setIsLoading(false);
          }, 500);
        }
      }, 150);

      return () => clearInterval(demoInterval);
    }
  }, [predictionId, navigate]);

  // Word-by-word reveal with planetary icons
  const startStory = () => {
    if (audioPlaying) {
      stopStory();
      return;
    }

    setAudioPlaying(true);
    setCurrentWordIndex(0);
    setVisibleIcons([]);
    setVisibleTimelineEvents([]);

    // Show timeline events progressively
    storyScript.timeline.forEach((event, idx) => {
      setTimeout(() => {
        setVisibleTimelineEvents(prev => [...prev, event]);
      }, idx * 2500);
    });

    // Word reveal interval - SLOWER for 5-min narration
    let wordIdx = 0;
    wordIntervalRef.current = setInterval(() => {
      if (wordIdx < storyWords.length) {
        setCurrentWordIndex(wordIdx);
        
        // Check if this word should trigger a planetary icon
        const word = storyWords[wordIdx];
        const iconMatch = storyScript.icons.find(icon => 
          word.text.toUpperCase().includes(icon.keyword) || word.text.includes(icon.keyword)
        );
        
        if (iconMatch && !visibleIcons.some(i => i.planet === iconMatch.planet)) {
          setVisibleIcons(prev => [...prev, iconMatch]);
        }
        
        // Check if this word should trigger a domain icon
        const domainMatch = storyScript.domainIcons.find(domain => 
          word.text.toUpperCase().includes(domain.keyword) || word.text.includes(domain.keyword)
        );
        
        if (domainMatch && !visibleIcons.some(i => i.house === domainMatch.house)) {
          setVisibleIcons(prev => [...prev, domainMatch]);
        }
        
        wordIdx++;
      } else {
        stopStory();
      }
    }, 300); // 300ms per word = ~5 minutes for full script
  };

  const stopStory = () => {
    setAudioPlaying(false);
    if (wordIntervalRef.current) {
      clearInterval(wordIntervalRef.current);
    }
  };

  const cosmicBackground = {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at center, #0a1628 0%, #020712 100%)',
    padding: '16px 20px',
    position: 'relative',
    overflow: 'auto'
  };

  const starsStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(2px 2px at 20% 30%, white, transparent),
      radial-gradient(2px 2px at 60% 70%, white, transparent),
      radial-gradient(1px 1px at 50% 50%, white, transparent),
      radial-gradient(1px 1px at 80% 10%, white, transparent)
    `,
    backgroundSize: '100vw 100vh',
    opacity: 0.5,
    zIndex: 0,
    pointerEvents: 'none'
  };

  if (isLoading) {
    return (
      <div style={cosmicBackground}>
        <div style={starsStyle} />
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1
        }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: 'center' }}
          >
            <p style={{
              fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              fontSize: '18px',
              color: '#edc161',
              marginBottom: '20px'
            }}>
              Preparing your life story...
            </p>
            <div style={{
              width: '200px',
              height: '4px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <motion.div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #edc161, #ffa500)',
                  borderRadius: '2px'
                }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      height: '100vh',
      background: 'radial-gradient(ellipse at center, #0a1628 0%, #020712 100%)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={starsStyle} />

      {/* Venn Diagram - Top Right */}
      <div style={{ 
        position: 'absolute', 
        top: '10px', 
        right: '10px',
        zIndex: 100
      }}>
        <svg width="80" height="50" viewBox="0 0 100 60" style={{ filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))' }}>
          <circle
            cx="32" cy="30" r="22"
            fill={selectedLanguage === 'hindi' ? 'rgba(237, 193, 97, 0.4)' : 'rgba(255, 255, 255, 0.08)'}
            stroke={selectedLanguage === 'hindi' ? 'rgba(237, 193, 97, 0.8)' : 'rgba(255, 255, 255, 0.2)'}
            strokeWidth="2"
            style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
            onClick={() => setSelectedLanguage('hindi')}
          />
          <circle
            cx="68" cy="30" r="22"
            fill={selectedLanguage === 'english' ? 'rgba(237, 193, 97, 0.4)' : 'rgba(255, 255, 255, 0.08)'}
            stroke={selectedLanguage === 'english' ? 'rgba(237, 193, 97, 0.8)' : 'rgba(255, 255, 255, 0.2)'}
            strokeWidth="2"
            style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
            onClick={() => setSelectedLanguage('english')}
          />
          <ellipse
            cx="50" cy="30" rx="14" ry="20"
            fill={selectedLanguage === 'hinglish' ? 'rgba(237, 193, 97, 0.5)' : 'rgba(255, 255, 255, 0.15)'}
            stroke={selectedLanguage === 'hinglish' ? 'rgba(237, 193, 97, 0.9)' : 'rgba(255, 255, 255, 0.3)'}
            strokeWidth="2"
            style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
            onClick={() => setSelectedLanguage('hinglish')}
          />
          <text x="32" y="34" textAnchor="middle" fill={selectedLanguage === 'hindi' ? '#edc161' : '#9CA3AF'}
            fontSize="14" fontWeight="700" style={{ pointerEvents: 'none' }}>H</text>
          <text x="68" y="34" textAnchor="middle" fill={selectedLanguage === 'english' ? '#edc161' : '#9CA3AF'}
            fontSize="14" fontWeight="700" style={{ pointerEvents: 'none' }}>E</text>
        </svg>
      </div>

      {/* Main Content - NO SCROLL */}
      <div style={{ 
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto 1fr auto',
        gap: '10px',
        padding: '60px 15px 15px 15px',
        maxWidth: '100%',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        
        {/* Top: Sage + Story Text */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '15px',
          minHeight: '0'
        }}>
          {/* Sage Avatar - Smaller */}
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{ flexShrink: 0 }}
          >
            <img 
              src="/sage-final.png" 
              alt="Sage"
              style={{
                width: 'clamp(80px, 15vw, 120px)',
                height: 'auto',
                opacity: audioPlaying ? 0.75 : 0.65,
                filter: audioPlaying 
                  ? 'drop-shadow(0 0 20px rgba(237, 193, 97, 0.5))' 
                  : 'drop-shadow(0 0 10px rgba(237, 193, 97, 0.3))',
                transition: 'all 0.6s ease'
              }}
            />
          </motion.div>

          {/* Story Text - Compact */}
          <div style={{
            flex: 1,
            padding: '15px',
            background: 'linear-gradient(135deg, rgba(237, 193, 97, 0.08), rgba(255, 153, 51, 0.05))',
            backdropFilter: 'blur(15px)',
            borderRadius: '16px',
            border: '1px solid rgba(237, 193, 97, 0.2)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            fontSize: 'clamp(12px, 2.5vw, 15px)',
            lineHeight: '1.5',
            minHeight: '0',
            maxHeight: '25vh',
            overflowY: 'auto'
          }}>
            {storyWords.slice(0, currentWordIndex + 1).map((word, idx) => (
              <motion.span
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: 'inline-block',
                  margin: '0 3px',
                  color: word.isHighlight ? '#edc161' : '#E8E8E8',
                  fontWeight: word.isHighlight ? 700 : 400,
                  textShadow: word.isHighlight ? '0 0 8px rgba(237, 193, 97, 0.6)' : 'none'
                }}
              >
                {word.text}
              </motion.span>
            ))}
          </div>
        </div>

        {/* Middle: Dynamic Icons Row - Planets & Domains */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(55px, 1fr))',
          gap: '8px',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px 5px',
          minHeight: '70px',
          maxWidth: '100%'
        }}>
          <AnimatePresence>
            {visibleIcons.map((icon, idx) => (
              <motion.div
                key={icon.planet || icon.house || idx}
                initial={{ opacity: 0, scale: 0, rotate: -180 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ 
                  duration: 1, 
                  delay: idx * 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
                style={{
                  width: 'clamp(50px, 9vw, 60px)',
                  height: 'clamp(50px, 9vw, 60px)',
                  borderRadius: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(22px, 4.5vw, 28px)',
                  background: `radial-gradient(circle, ${icon.color ? icon.color + '30' : 'rgba(237, 193, 97, 0.2)'}, transparent)`,
                  color: icon.color || '#edc161',
                  boxShadow: `0 0 25px ${icon.color || 'rgba(237, 193, 97, 0.5)'}`,
                  border: `2px solid ${icon.color ? icon.color + '60' : 'rgba(237, 193, 97, 0.4)'}`,
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                whileHover={{ scale: 1.1 }}
                title={icon.description || icon.keyword}
              >
                {icon.symbol}
                {icon.house && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-2px',
                    fontSize: 'clamp(8px, 1.5vw, 10px)',
                    fontWeight: 700,
                    color: icon.color || '#edc161',
                    background: 'rgba(10, 22, 40, 0.9)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    border: `1px solid ${icon.color || '#edc161'}`
                  }}>
                    H{icon.house}
                  </span>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Bottom: Vertical Timeline with Scroll Snap - Dynamic & Slow */}
        <div style={{ 
          minHeight: '0',
          maxHeight: '42vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(237, 193, 97, 0.3) transparent',
          position: 'relative'
        }}>
          <h3 style={{
            textAlign: 'center',
            fontSize: 'clamp(14px, 3vw, 18px)',
            fontWeight: 700,
            color: '#edc161',
            marginBottom: '12px',
            letterSpacing: '1px',
            textShadow: '0 0 15px rgba(237, 193, 97, 0.5)',
            position: 'sticky',
            top: 0,
            background: 'rgba(10, 22, 40, 0.9)',
            backdropFilter: 'blur(10px)',
            padding: '8px 0',
            zIndex: 10
          }}>
            जीवन यात्रा | Life Journey
          </h3>

          {/* Vertical Timeline with connecting line */}
          <div style={{ 
            position: 'relative',
            padding: '10px 20px 10px 40px'
          }}>
            {/* Vertical connecting line */}
            <div style={{
              position: 'absolute',
              left: '15px',
              top: '10px',
              bottom: '10px',
              width: '2px',
              background: 'linear-gradient(to bottom, transparent, rgba(237, 193, 97, 0.6), rgba(237, 193, 97, 0.6), transparent)',
              zIndex: 1
            }} />

            <AnimatePresence>
              {visibleTimelineEvents.map((event, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -30, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: idx * 0.2,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                  style={{
                    scrollSnapAlign: 'start',
                    marginBottom: '15px',
                    position: 'relative'
                  }}
                >
                  {/* Timeline dot with icon */}
                  <div style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '12px',
                    width: 'clamp(28px, 5vw, 36px)',
                    height: 'clamp(28px, 5vw, 36px)',
                    background: event.highlight 
                      ? `radial-gradient(circle, ${event.color || '#edc161'}, transparent)`
                      : 'rgba(237, 193, 97, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'clamp(14px, 3vw, 18px)',
                    border: `2px solid ${event.highlight ? (event.color || '#edc161') : 'rgba(237, 193, 97, 0.4)'}`,
                    boxShadow: event.highlight 
                      ? `0 0 20px ${event.color || 'rgba(237, 193, 97, 0.8)'}`
                      : '0 0 10px rgba(237, 193, 97, 0.3)',
                    zIndex: 2,
                    animation: event.highlight ? 'pulse 2s infinite' : 'none'
                  }}>
                    {event.icon || '●'}
                  </div>

                  {/* Event card */}
                  <div style={{
                    padding: 'clamp(10px, 2vw, 14px)',
                    background: event.highlight 
                      ? `linear-gradient(135deg, ${event.color ? event.color + '25' : 'rgba(237, 193, 97, 0.15)'}, rgba(255, 153, 51, 0.1))`
                      : 'linear-gradient(135deg, rgba(237, 193, 97, 0.08), rgba(255, 153, 51, 0.05))',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '12px',
                    border: `1px solid ${event.highlight ? (event.color ? event.color + '60' : 'rgba(237, 193, 97, 0.4)') : 'rgba(237, 193, 97, 0.2)'}`,
                    boxShadow: event.highlight 
                      ? `0 6px 20px ${event.color ? event.color + '30' : 'rgba(237, 193, 97, 0.3)'}`
                      : '0 4px 12px rgba(0, 0, 0, 0.3)',
                    transform: event.highlight ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.3s ease'
                  }}>
                    {/* Year and Domain badge */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '6px'
                    }}>
                      <div style={{
                        fontSize: 'clamp(15px, 3vw, 18px)',
                        fontWeight: 800,
                        color: event.highlight ? (event.color || '#edc161') : '#edc161',
                        textShadow: event.highlight ? `0 0 12px ${event.color || 'rgba(237, 193, 97, 0.6)'}` : '0 0 8px rgba(237, 193, 97, 0.4)'
                      }}>
                        {event.year}
                      </div>
                      {event.domain && (
                        <div style={{
                          fontSize: 'clamp(8px, 1.5vw, 10px)',
                          padding: '3px 8px',
                          background: event.highlight ? (event.color ? event.color + '40' : 'rgba(237, 193, 97, 0.3)') : 'rgba(237, 193, 97, 0.15)',
                          borderRadius: '10px',
                          color: '#E8E8E8',
                          fontWeight: 600,
                          letterSpacing: '0.5px'
                        }}>
                          {event.domain}
                        </div>
                      )}
                    </div>

                    {/* Event description */}
                    <div style={{
                      fontSize: 'clamp(11px, 2.2vw, 13px)',
                      lineHeight: '1.5',
                      color: '#E8E8E8',
                      fontWeight: event.highlight ? 500 : 400
                    }}>
                      {event.event}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Play Button - Bottom Center */}
      <div style={{
        position: 'absolute',
        bottom: '15px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100
      }}>
        <button
          onClick={audioPlaying ? stopStory : startStory}
          style={{
            padding: 'clamp(10px, 2vw, 14px) clamp(20px, 4vw, 35px)',
            background: audioPlaying 
              ? 'linear-gradient(135deg, rgba(220, 20, 60, 0.3), rgba(255, 100, 100, 0.3))'
              : 'linear-gradient(135deg, rgba(237, 193, 97, 0.3), rgba(255, 215, 0, 0.3))',
            border: `2px solid ${audioPlaying ? 'rgba(220, 20, 60, 0.6)' : 'rgba(237, 193, 97, 0.6)'}`,
            borderRadius: '25px',
            color: audioPlaying ? '#DC143C' : '#edc161',
            fontSize: 'clamp(12px, 2.5vw, 16px)',
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.5px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 16px rgba(237, 193, 97, 0.4)',
            whiteSpace: 'nowrap'
          }}
        >
          {audioPlaying ? '⏸ Pause' : '▶ Start'}
        </button>
      </div>
    </div>
  );
};

export default PastStoryUnifiedCinematic;
